import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence, useSpring, useTransform } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import TutorialInline, { TutorialWelcomeModal } from "@/components/TutorialPopup";
import { resetAllTutorials } from "@/lib/tutorials";
import {
  Activity, Users, DollarSign, Calendar, FileText, Zap, Bell,
  KanbanSquare, ClipboardList, Sparkles, ShieldCheck, Lock,
  Package, Database, Bug, Building2, Cable, UserSquare,
  Plus, Loader2, ChevronRight, Timer, RefreshCw, CreditCard,
  CheckCircle2, ArrowRight, Play, X, TrendingUp, Star,
  PhoneCall, MapPin, Wrench, ClipboardCheck, Camera, Receipt,
  BadgeCheck, TrendingDown as TrendingLedger,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================
   SCORE 10 CONTENT (drawer coaching overlays)
   Keyed by drawer.key — every interactive card carries one.
   ============================================================ */
const SCORE10_MAP = {
  crm:       { title: "10/10 Customer Intake & Pipeline", body: "To score a 10, eliminate friction points during initial contact. An automated, zero-wait intake portal captures job details and photos up front, sending instant SMS confirmations so the customer knows help is on the way." },
  intake:    { title: "10/10 Zero-Touch Job Prep",         body: "To score a 10, capture equipment models and photo attachments automatically at initial intake to eliminate back-and-forth phone calls and pre-stage field inventory." },
  pay:       { title: "10/10 Immediate Cash Flow",         body: "To score a 10, collect payment before leaving the driveway. Mobile tap-to-pay and automated text payment links drop average collection times down to zero days." },
  inv:       { title: "10/10 Instant Digital Billing",     body: "To score a 10, convert signed estimates into itemized digital invoices automatically the second work is completed, eliminating back-office entry delays." },
  kanban:    { title: "10/10 Smart Route Dispatching",     body: "To score a 10, use location-aware routing and real-time job status tracking to cut transit costs, maximize technician billable hours, and update clients automatically." },
  analytics: { title: "10/10 Live General Ledger Sync",    body: "To score a 10, sync captured payments instantly to the general ledger, giving management complete visibility into exact daily gross margins and available working capital." },
  sched:     { title: "10/10 Arrival-Window Precision",    body: "To score a 10, commit to a 30-minute arrival window with live GPS tracking texts. Customers stop calling for updates and NPS scores jump 20+ points." },
  wf:        { title: "10/10 Zero-Touch Automation",       body: "To score a 10, replace every manual handoff with a rule: job closed → CSAT SMS → 6-month reminder → auto-quote. The platform runs 24/7 while you sleep." },
  docs:      { title: "10/10 Bulletproof Legal Cover",     body: "To score a 10, capture before/after photos and signed digital forms on every job. Disputes evaporate and insurance claims are pre-documented." },
  notify:    { title: "10/10 Automated Follow-Through",    body: "To score a 10, trigger email + SMS on every status change. Customers feel served, no one falls through the cracks, and rebooking rates spike." },
};

/* ============================================================
   FIELD SERVICES · 10-STEP REVENUE FLOW
   Numbered coaching deck that replaces the generic tile grid.
   Each step maps to (existing) drawer body + its own coaching card.
   ============================================================ */
/* ============================================================
   INDUSTRY 10-STEP REVENUE FLOWS
   Same visual layout across all 3 verticals; step titles + Score 10
   strategies are tuned per business. Every step maps to an existing
   drawer body via `key`.
   ============================================================ */
const INDUSTRY_FLOWS = {
  field_services: [
    { step: 1,  key: "intake",   Icon: PhoneCall,       title: "Inbound Service Request",       stat: "Zero-friction intake",   drawerTitle: "Automated Intake Portal",
      score: { title: "10/10 Inbound Intake",           body: "Eliminate every friction point during initial contact. Use an automated, zero-wait intake portal that captures job details, equipment models, and photos up front. Send instant SMS confirmations so the customer knows help is on the way." } },
    { step: 2,  key: "kanban",   Icon: MapPin,          title: "Dispatch & Route Scheduling",   stat: "GPS-aware routing",      drawerTitle: "Dispatch & Route Board",
      score: { title: "10/10 Automated Dispatch",       body: "Replace manual scheduling with intelligent location-aware dispatching. Use real-time GPS tracking to route the closest tech with the right parts, cutting transit costs. Send customers automated updates with exact arrival windows and live route tracking." } },
    { step: 3,  key: "docs",     Icon: ClipboardCheck,  title: "On-Site Inspection",            stat: "Digital checklist",      drawerTitle: "Inspection Checklist & Photos",
      score: { title: "10/10 On-Site Diagnostic",       body: "Standardize digital mobile checklists instead of paper notes. Technicians must conduct visual inspections, capture 'before' photos, and log findings directly into the job file to protect the business legally and build customer transparency." } },
    { step: 4,  key: "inv",      Icon: FileText,        title: "Digital Estimate · Sign-Off",   stat: "Good / Better / Best",   drawerTitle: "Tiered Estimate & E-Signature",
      score: { title: "10/10 Instant Estimate",         body: "Offer flexible 'Good/Better/Best' tiered pricing options right on tablet screens. Present transparent line items without pressure, and secure an electronic signature on the spot to lock in job approvals instantly." } },
    { step: 5,  key: "kanban",   Icon: Wrench,          title: "Job Execution · Parts Tracking",stat: "Live labor + inventory", drawerTitle: "Job Board · Advance stage",
      score: { title: "10/10 Job Execution",            body: "Integrate real-time inventory and labor tracking directly into the technician's mobile workflow. Log every part pulled from truck stock alongside live timers to capture every billable minute accurately and prevent cost overruns." } },
    { step: 6,  key: "docs",     Icon: Camera,          title: "Customer Sign-Off · After-Photos", stat: "Digital signature",    drawerTitle: "Completion Photos & Sign-Off",
      score: { title: "10/10 Customer Sign-Off",        body: "Eliminate post-job disputes by capturing high-resolution 'after' photos showing completed work. Demonstrate the repair to the customer and secure a final digital sign-off before leaving the driveway." } },
    { step: 7,  key: "inv",      Icon: Receipt,         title: "On-Site Invoice Generation",    stat: "Auto quote → invoice",   drawerTitle: "Instant Itemized Invoice",
      score: { title: "10/10 Real-Time Invoice",        body: "Eliminate back-office data entry delays by converting signed estimates into final itemized invoices automatically. Compile parts, labor, warranties, and taxes into a clean digital bill immediately upon job completion." } },
    { step: 8,  key: "pay",      Icon: CreditCard,      title: "Immediate Payment Capture",     stat: "Stripe · Tap · Link",    drawerTitle: "Stripe Terminal · Tap to Pay",
      score: { title: "10/10 Zero-Day Collection",      body: "Collect payment before the service vehicle leaves the job site. Equip techs with mobile terminals for credit cards, tap-to-pay, or instant SMS payment links to drop average collection times to zero." } },
    { step: 9,  key: "analytics",Icon: TrendingUp,      title: "Deposit · Ledger · GL Sync",    stat: "Real-time books",        drawerTitle: "Live Ledger + Accounting Sync",
      score: { title: "10/10 Automated Bookkeeping",    body: "Automate bookkeeping instantly. Captured payments must automatically post to the general ledger, update daily revenue counters, and sync with accounting software to give management real-time margin visibility." } },
    { step: 10, key: "notify",   Icon: BadgeCheck,      title: "Closeout · Review · Recurring", stat: "Auto CSAT + Rebook",     drawerTitle: "Closeout · Review · Maintenance Plan",
      score: { title: "10/10 Lifetime Recurring Revenue", body: "Turn one-off repairs into lifetime recurring revenue. Automatically send a text review link immediately after the job, and auto-enroll the client into a 6-month service plan to lock in future business." } },
  ],
  legal_finance: [
    { step: 1,  key: "intake",   Icon: PhoneCall,       title: "New Matter · Inquiry Intake",   stat: "Prospect qualified in 60s", drawerTitle: "New Client Inquiry Portal",
      score: { title: "10/10 First Contact",             body: "Capture every inquiry with a smart intake form that pre-qualifies matter type, urgency, and budget before it ever hits a paralegal's desk. Auto-route hot leads to the right associate within minutes." } },
    { step: 2,  key: "docs",     Icon: ShieldCheck,     title: "Conflict Check · KYC",          stat: "Ethics compliant",          drawerTitle: "Automated Conflict Search",
      score: { title: "10/10 Bar-Compliant Onboarding",  body: "Run automated conflict-of-interest searches against every existing matter before you sign a retainer. Fail-safe compliance protects your bar license and your book of business." } },
    { step: 3,  key: "inv",      Icon: FileText,        title: "Engagement Letter · Retainer",  stat: "E-sign in-portal",          drawerTitle: "Engagement Letter Builder",
      score: { title: "10/10 Retainer Locked",           body: "Convert a signed proposal into an engagement letter and trust-account retainer invoice in one click. Clients sign on any device and the retainer clears before day-one work begins." } },
    { step: 4,  key: "kanban",   Icon: ClipboardCheck,  title: "Discovery · Case Prep",         stat: "Matter pipeline",           drawerTitle: "Matter Board · Advance stage",
      score: { title: "10/10 Discovery Pipeline",        body: "Track every matter through Intake → Discovery → Review → Closed on a single board. Deadlines auto-alert the associate the moment status changes, so nothing slips past a statute of limitations." } },
    { step: 5,  key: "docs",     Icon: Camera,          title: "Evidence · Brief Filing",       stat: "Version-controlled",        drawerTitle: "Legal Document Vault",
      score: { title: "10/10 Bulletproof Records",       body: "Store every brief, exhibit, and filing in a version-controlled vault with automatic redaction and privileged-doc flags. Discovery response goes from weeks to hours." } },
    { step: 6,  key: "sched",    Icon: Calendar,        title: "Court Dates · Client Meetings", stat: "Auto-remind clients",       drawerTitle: "Consultation & Docket Calendar",
      score: { title: "10/10 Never a Missed Date",       body: "Sync every court date, deposition, and client meeting to associates + clients with automated reminders 72h and 24h out. Missed appearances drop to zero." } },
    { step: 7,  key: "analytics",Icon: Timer,           title: "Billable Time · Expense Capture", stat: "Live timers",             drawerTitle: "Billable Hour Tracking",
      score: { title: "10/10 Every Minute Captured",     body: "Live timers on every matter capture billable minutes as they happen. Expense receipts photograph straight to the file. Associates recover 3–6 hidden hours per week." } },
    { step: 8,  key: "inv",      Icon: Receipt,         title: "Trust Draw · Bill Generation",  stat: "Trust-compliant",           drawerTitle: "Bill Builder · Trust Account",
      score: { title: "10/10 Trust-Compliant Billing",   body: "Draw against trust and issue itemized bills in one motion. Every entry references its time slip so bills are audit-proof and clients pay without pushback." } },
    { step: 9,  key: "pay",      Icon: CreditCard,      title: "Payment · Retainer Refill",     stat: "ACH · Card · Link",         drawerTitle: "Payments & Retainer Refill",
      score: { title: "10/10 Fast Collections",          body: "Accept card, ACH, and payment-link retainer refills straight from the bill email. Aged AR shrinks from 90 days to under 15." } },
    { step: 10, key: "notify",   Icon: BadgeCheck,      title: "Matter Closure · Client Retention", stat: "Referral loop",         drawerTitle: "Closeout · Referral · Follow-up",
      score: { title: "10/10 Lifetime Client Value",     body: "On matter close, auto-request a review, mail a thank-you, and add the client to a quarterly check-in cadence. Referrals compound and cost-per-acquisition trends toward zero." } },
  ],
  health_wellness: [
    { step: 1,  key: "intake",   Icon: PhoneCall,       title: "Patient Referral · Inquiry",    stat: "Zero-wait intake",       drawerTitle: "Patient Intake Portal",
      score: { title: "10/10 Frictionless Referral",     body: "Convert every referral fax, web form, or phone call into a digital record in under 60 seconds. Insurance card and ID photo capture at intake means the front desk never chases paperwork again." } },
    { step: 2,  key: "docs",     Icon: ShieldCheck,     title: "Insurance Verification · HIPAA",stat: "Auto eligibility check", drawerTitle: "Insurance & HIPAA Vault",
      score: { title: "10/10 Verified Before Visit",     body: "Auto-verify insurance eligibility the moment a patient books. HIPAA-secure document vault stores signed consents so denials from missing paperwork drop to zero." } },
    { step: 3,  key: "sched",    Icon: Calendar,        title: "Session Scheduling",            stat: "Self-serve booking",     drawerTitle: "Appointment Booking",
      score: { title: "10/10 Full Book of Business",     body: "Let patients self-schedule from a portal with real-time practitioner availability. Automated reminders 48h and 2h out slash no-show rates below 5%." } },
    { step: 4,  key: "inv",      Icon: ClipboardCheck,  title: "Evaluation · Treatment Plan",   stat: "Sign-off on tablet",     drawerTitle: "Treatment Plan Builder",
      score: { title: "10/10 Plan Adherence",            body: "Present a visual, tiered treatment plan on the tablet at initial eval. Patients see the arc of care, sign off, and commit — plan completion rates jump 30%." } },
    { step: 5,  key: "kanban",   Icon: Wrench,          title: "Session Delivery · Progress Notes", stat: "Charting in seconds", drawerTitle: "Treatment Board · Advance stage",
      score: { title: "10/10 Documented Care",           body: "Practitioners chart via templated SOAP notes tied to the treatment plan. Every session auto-advances the patient on the care board so no one gets lost between visits." } },
    { step: 6,  key: "docs",     Icon: Camera,          title: "Progress Photos · Outcome Logs",stat: "Visual proof of care",   drawerTitle: "Progress Photos & Outcomes",
      score: { title: "10/10 Outcome-Driven Care",       body: "Capture before/after photos, ROM measurements, and pain scores at every visit. Visual proof drives patient buy-in and gives insurers audit-proof outcomes for authorization renewals." } },
    { step: 7,  key: "inv",      Icon: Receipt,         title: "Superbill · Charge Capture",    stat: "Auto CPT codes",         drawerTitle: "Superbill Generator",
      score: { title: "10/10 Clean Claims",              body: "Auto-generate CPT-coded superbills from session notes the moment charting is done. Front-office rejection rates fall and cash flows in 15 days instead of 45." } },
    { step: 8,  key: "pay",      Icon: CreditCard,      title: "Copay · Claim Submission",      stat: "Card · HSA · Payer",     drawerTitle: "Copay & Insurance Claim",
      score: { title: "10/10 Point-of-Care Collection",  body: "Collect the copay before the patient walks out — card, HSA, or tap-to-pay. Claims file to the payer automatically the same night. Aged AR essentially disappears." } },
    { step: 9,  key: "analytics",Icon: TrendingUp,      title: "Revenue Cycle · Posting",       stat: "Live collections",       drawerTitle: "Revenue Cycle Dashboard",
      score: { title: "10/10 Real-Time RCM",             body: "Every payment posts to the ledger the moment it lands. Owners see collections, days-in-AR, and payer mix live — no more month-end surprises." } },
    { step: 10, key: "notify",   Icon: BadgeCheck,      title: "Discharge · Wellness Plan",     stat: "Auto follow-up cadence", drawerTitle: "Discharge · Reviews · Wellness Plan",
      score: { title: "10/10 Lifetime Wellness Program", body: "On discharge, auto-request a review, mail a thank-you, and enroll the patient in a maintenance-visit cadence. One-time patients become lifetime wellness clients." } },
  ],
};

/* ============================================================
   Per-industry accent palette — the whole page re-themes to match
   ============================================================ */
const INDUSTRY_THEME = {
  field_services: {
    primary:  "#1E40AF",  // electric indigo
    primarySoft: "#DBEAFE",
    accent:   "#0891B2",  // cyan
    label:    "Field Services",
    short:    "Field",
  },
  legal_finance: {
    primary:  "#6D28D9",  // deep violet
    primarySoft: "#EDE9FE",
    accent:   "#B45309",  // amber
    label:    "Legal & Finance",
    short:    "Legal",
  },
  health_wellness: {
    primary:  "#059669",  // emerald
    primarySoft: "#D1FAE5",
    accent:   "#0891B2",  // teal
    label:    "Health & Wellness",
    short:    "Health",
  },
};

/* ============================================================
   Animated count-up hook (framer spring on any numeric value)
   ============================================================ */
const AnimatedNumber = ({ value, prefix = "", suffix = "", decimals = 0, className = "" }) => {
  const spring = useSpring(0, { stiffness: 90, damping: 22, mass: 1 });
  const display = useTransform(spring, (v) => {
    const n = decimals ? v.toFixed(decimals) : Math.round(v);
    return `${prefix}${Number(n).toLocaleString()}${suffix}`;
  });
  useEffect(() => { spring.set(Number(value) || 0); }, [value, spring]);
  return <motion.span className={className}>{display}</motion.span>;
};

/* ============================================================
   Countdown badge — 48h for public sessions
   ============================================================ */
const useCountdown = (expiresAt) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return "Expired";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${h}h ${m}m ${s}s`;
};

/* ============================================================
   ROOT PAGE
   ============================================================ */
const SandboxPage = ({ isPresenter = false }) => {
  const nav = useNavigate();
  const { sessionId: urlSessionId } = useParams();
  const [session, setSession] = useState(null);
  const [preset, setPreset] = useState(null);
  const [records, setRecords] = useState([]);
  const [activity, setActivity] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState(null);
  const [drawer, setDrawer] = useState(null);           // { key, title, body }
  const [ripple, setRipple] = useState(null);           // { x, y, key }

  const theme = INDUSTRY_THEME[session?.industry] || INDUSTRY_THEME.field_services;

  const refresh = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const [s, r, a] = await Promise.all([
        axios.get(`${API}/sandbox/session/${sid}`),
        axios.get(`${API}/sandbox/session/${sid}/records`),
        axios.get(`${API}/sandbox/session/${sid}/activity?limit=25`),
      ]);
      setSession(s.data.session);
      setPreset(s.data.industry_preset);
      setRecords(r.data.items);
      setActivity(a.data.items);
    } catch (err) {
      toast.error("Sandbox session unavailable — starting a fresh one.");
      localStorage.removeItem("nex_sandbox_session");
      window.location.href = "/sandbox";
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const inds = await axios.get(`${API}/sandbox/industries`);
        setIndustries(inds.data.industries);
      } catch { /* ignore */ }
      let sid = urlSessionId || localStorage.getItem("nex_sandbox_session");
      if (!sid) {
        const { data } = await axios.post(`${API}/sandbox/session`, { industry: "field_services" });
        sid = data.session.id;
        localStorage.setItem("nex_sandbox_session", sid);
      }
      await refresh(sid);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!session?.id) return;
    const t = setInterval(() => refresh(session.id), 5000);
    return () => clearInterval(t);
  }, [session?.id, refresh]);

  const switchIndustry = async (key) => {
    if (!session?.id || key === session.industry) return;
    setLoading(true);
    await axios.post(`${API}/sandbox/session/${session.id}/switch`, { industry: key });
    await refresh(session.id);
    setLoading(false);
    toast.success(`Re-skinned to ${INDUSTRY_THEME[key]?.label}`);
  };

  const purge = async () => {
    if (!session?.id) return;
    if (!window.confirm("Reset the sandbox for a fresh pitch? All demo data will be wiped.")) return;
    try {
      const token = localStorage.getItem("nex_token");
      await axios.post(`${API}/sandbox/session/${session.id}/purge`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await refresh(session.id);
      toast.success("Sandbox reset · ready for the next prospect");
    } catch {
      toast.error("Sign in as an admin to purge.");
    }
  };

  const fireRipple = (e) => {
    if (!e) return;
    setRipple({ x: e.clientX, y: e.clientY, key: Math.random() });
    setTimeout(() => setRipple(null), 900);
  };

  const countdown = useCountdown(session?.expires_at);
  const kpis = session?.kpis || {};
  const contacts    = records.filter(r => r.kind === "contact");
  const jobs        = records.filter(r => r.kind === "job");
  const invoices    = records.filter(r => r.kind === "invoice");
  const appointments= records.filter(r => r.kind === "appointment");
  const docs        = records.filter(r => r.kind === "document");

  if (loading || !session || !preset) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} style={{ color: theme.primary }} />
      </div>
    );
  }

  const t = preset.terminology;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans">
      {/* GLOBAL RIPPLE */}
      <AnimatePresence>
        {ripple && (
          <motion.div
            key={ripple.key}
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 8, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="fixed pointer-events-none rounded-full z-[60]"
            style={{
              left: ripple.x - 24, top: ripple.y - 24,
              width: 48, height: 48,
              background: theme.primary + "33",
              border: `2px solid ${theme.primary}`,
            }}
          />
        )}
      </AnimatePresence>

      {/* Non-blocking main welcome modal (shared across the platform) —
          plays any file-uploaded clip from /admin/tutorials if one is
          set for the "main.overview" slot. */}
      <TutorialWelcomeModal tutorialKey="main.overview" />
      {/* Non-blocking per-industry overview — plays the first time a
          prospect enters each vertical. `waitForKey` makes it stand down
          while the shared main welcome is still pending, so we never
          have two videos auto-playing at once. */}
      <TutorialWelcomeModal
        key={session.industry}
        tutorialKey={`${session.industry}.overview`}
        autoOpenDelayMs={1500}
        waitForKey="main.overview"
      />

      {/* HEADER */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm shrink-0"
                 style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}>
              <Sparkles size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-900 tracking-tight text-lg leading-tight">NEXENGINES</div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: theme.primary }}>
                Sandbox{isPresenter ? " · Presenter" : ""}
              </div>
            </div>
            {/* Live pulse */}
            <span className="ml-2 flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {isPresenter && (
              <button
                onClick={() => { resetAllTutorials(); toast.success("Tutorials reset · will reshow on next visit"); }}
                data-testid="reset-tutorials-btn"
                className="text-[11px] font-semibold uppercase tracking-wider px-3 py-2 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 flex items-center gap-1.5 transition-all"
              >
                <Play size={12} /> Reset Tutorials
              </button>
            )}
            {isPresenter && (
              <button onClick={purge} data-testid="purge-btn"
                className="text-[11px] font-semibold uppercase tracking-wider px-3 py-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1.5 transition-all">
                <RefreshCw size={12} /> Reset
              </button>
            )}
            {countdown && !isPresenter && (
              <span data-testid="session-countdown" className="text-[11px] font-semibold uppercase tracking-wider px-3 py-2 rounded-lg bg-slate-100 text-slate-700 flex items-center gap-1.5">
                <Timer size={12} style={{ color: theme.primary }} /> {countdown}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* FLOATING INDUSTRY SWITCHER */}
      <div className="max-w-[1500px] mx-auto px-5 lg:px-10 pt-6">
        <div className="flex gap-2 p-1.5 bg-white rounded-2xl border border-slate-200 shadow-md inline-flex">
          {industries.map(i => {
            const th = INDUSTRY_THEME[i.key];
            const active = session.industry === i.key;
            return (
              <button
                key={i.key}
                onClick={() => switchIndustry(i.key)}
                data-testid={`industry-${i.key}`}
                className="relative text-[11px] font-bold uppercase tracking-widest px-4 py-2.5 rounded-xl transition-all"
                style={{
                  background: active ? `linear-gradient(135deg, ${th.primary}, ${th.accent})` : "transparent",
                  color: active ? "#fff" : "#475569",
                  boxShadow: active ? `0 8px 20px -8px ${th.primary}` : "none",
                }}
              >
                {th.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* KPI STRIP · animated */}
      <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label={preset.kpi_labels.revenue}  value={kpis.revenue_usd || 0} prefix="$" theme={theme} Icon={TrendingUp} testid="kpi-revenue" />
        <KpiCard label={preset.kpi_labels.pipeline} value={kpis.open_pipeline || 0} theme={theme} Icon={KanbanSquare} testid="kpi-pipeline" />
        <KpiCard label={preset.kpi_labels.active}   value={kpis.staff_active || 0} theme={theme} Icon={Users} testid="kpi-active" />
        <KpiCard label={preset.kpi_labels.avg}      value={kpis.avg_ticket || 0} prefix="$" theme={theme} Icon={DollarSign} testid="kpi-avg" />
      </div>

      {/* MAIN GRID: showcase deck + activity rail */}
      <div className="max-w-[1500px] mx-auto px-5 lg:px-10 pb-14 grid lg:grid-cols-[1fr_340px] gap-6">
        <div>
          <div className="mb-4 flex items-baseline justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: theme.primary }}>
                20 · Capabilities Showcase
              </div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-1">
                Every operation your business runs — in one platform
              </h2>
            </div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
              {preset.tagline}
            </div>
          </div>

          {/* Industry-specific 10-step numbered revenue flow (all 3 verticals) */}
          {INDUSTRY_FLOWS[session.industry] && (
            <IndustryFlow
              steps={INDUSTRY_FLOWS[session.industry]}
              theme={theme}
              onOpen={(step, e) => {
                fireRipple(e);
                const cfg = {
                  crm:       { title: `Unified ${t.contact_plural} Pipeline`, body: <CrmBody preset={preset} session={session} refresh={refresh} contacts={contacts} /> },
                  intake:    { title: step.drawerTitle, body: <IntakeBody preset={preset} session={session} refresh={refresh} close={() => setDrawer(null)} /> },
                  pay:       { title: step.drawerTitle, body: <PaymentsBody preset={preset} invoices={invoices} onPay={(inv) => { setPayModal(inv); setDrawer(null); }} /> },
                  inv:       { title: step.drawerTitle, body: <InvoicingBody preset={preset} session={session} refresh={refresh} contacts={contacts} close={() => setDrawer(null)} /> },
                  kanban:    { title: step.drawerTitle, body: <KanbanBody preset={preset} session={session} refresh={refresh} jobs={jobs} /> },
                  analytics: { title: step.drawerTitle, body: <AnalyticsBody preset={preset} kpis={kpis} activity={activity} /> },
                  sched:     { title: step.drawerTitle, body: <SchedulingBody preset={preset} session={session} refresh={refresh} appointments={appointments} contacts={contacts} close={() => setDrawer(null)} /> },
                  wf:        { title: step.drawerTitle, body: <WorkflowBody preset={preset} session={session} refresh={refresh} fireRipple={fireRipple} /> },
                  docs:      { title: step.drawerTitle, body: <DocsBody preset={preset} session={session} refresh={refresh} docs={docs} /> },
                  notify:    { title: step.drawerTitle, body: <NotifyBody preset={preset} session={session} refresh={refresh} fireRipple={fireRipple} /> },
                }[step.key];
                setDrawer({ key: `${step.key}-${step.step}`, title: cfg.title, body: cfg.body, score: step.score, tutorialKey: `${session.industry}.step_${step.step}` });
              }}
            />
          )}

          {/* Full 20-tile capabilities deck — always visible so prospects can see the growth path */}
          <div className="mt-8 mb-4">
            <div className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: theme.primary }}>
              Full Platform Roadmap · 20 Capabilities
            </div>
            <div className="text-sm text-slate-600 mt-1">
              The 10 steps above are the sequence — these 20 are every operational surface we deploy for your business.
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InteractiveCard testid="card-crm" Icon={Users} title={`CRM · ${t.contact_plural}`} theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "crm", title: `Unified ${t.contact_plural} Pipeline`, body: <CrmBody preset={preset} session={session} refresh={refresh} contacts={contacts} />, score: SCORE10_MAP.crm }); }}
              stat={`${contacts.length} live`}
            />
            <InteractiveCard testid="card-intake" Icon={ClipboardList} title="Dynamic Intake" theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "intake", title: "Dynamic Intake Form", body: <IntakeBody preset={preset} session={session} refresh={refresh} close={() => setDrawer(null)} />, score: SCORE10_MAP.intake }); }}
              stat="Form → CRM in 0s"
            />
            <InteractiveCard testid="card-payments" Icon={CreditCard} title="Stripe Payments" theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "pay", title: "Simulated Stripe Terminal", body: <PaymentsBody preset={preset} invoices={invoices} onPay={(inv) => { setPayModal(inv); setDrawer(null); }} />, score: SCORE10_MAP.pay }); }}
              stat={`${invoices.filter(i => i.data.status !== "paid").length} open`}
            />
            <InteractiveCard testid="card-invoicing" Icon={DollarSign} title={`Automated ${t.invoice}`} theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "inv", title: `Issue ${t.invoice}`, body: <InvoicingBody preset={preset} session={session} refresh={refresh} contacts={contacts} close={() => setDrawer(null)} />, score: SCORE10_MAP.inv }); }}
              stat="Quote → paid link"
            />
            <InteractiveCard testid="card-kanban" Icon={KanbanSquare} title={`${t.job} Board`} theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "kanban", title: `${t.job} Board · Drag to advance`, body: <KanbanBody preset={preset} session={session} refresh={refresh} jobs={jobs} />, score: SCORE10_MAP.kanban }); }}
              stat={`${jobs.length} in flight`}
            />
            <InteractiveCard testid="card-analytics" Icon={Activity} title="Real-Time Analytics" theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "analytics", title: "Live Metrics", body: <AnalyticsBody preset={preset} kpis={kpis} activity={activity} />, score: SCORE10_MAP.analytics }); }}
              stat="Live"
            />
            <InteractiveCard testid="card-scheduling" Icon={Calendar} title={`${t.appointment} Booking`} theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "sched", title: `Book ${t.appointment}`, body: <SchedulingBody preset={preset} session={session} refresh={refresh} appointments={appointments} contacts={contacts} close={() => setDrawer(null)} />, score: SCORE10_MAP.sched }); }}
              stat={`${appointments.length} booked`}
            />
            <InteractiveCard testid="card-workflow" Icon={Zap} title="Workflow Logic" theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "wf", title: "When-This-Then-That Automation", body: <WorkflowBody preset={preset} session={session} refresh={refresh} fireRipple={fireRipple} />, score: SCORE10_MAP.wf }); }}
              stat="When / Then"
            />
            <InteractiveCard testid="card-docs" Icon={FileText} title="Document Vault" theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "docs", title: "Secure Documents", body: <DocsBody preset={preset} session={session} refresh={refresh} docs={docs} />, score: SCORE10_MAP.docs }); }}
              stat={`${docs.length} files`}
            />
            <InteractiveCard testid="card-notify" Icon={Bell} title="Notifications" theme={theme}
              onClick={(e) => { fireRipple(e); setDrawer({ key: "notify", title: "Email + SMS Broadcast", body: <NotifyBody preset={preset} session={session} refresh={refresh} fireRipple={fireRipple} />, score: SCORE10_MAP.notify }); }}
              stat="Email · SMS"
            />

            {/* Preview tiles — same visual family, subtle "coming in session" chip */}
            {PREVIEW_TILES.map(p => (
              <PreviewCard key={p.label} theme={theme} {...p} />
            ))}
          </div>
        </div>

        {/* ACTIVITY RAIL */}
        <ActivityRail activity={activity} theme={theme} />
      </div>

      {/* DRAWER */}
      <AnimatePresence>
        {drawer && (
          <Drawer key={drawer.key} onClose={() => setDrawer(null)} title={drawer.title} theme={theme} score={drawer.score} tutorialKey={drawer.tutorialKey}>
            {drawer.body}
          </Drawer>
        )}
      </AnimatePresence>

      {/* STRIPE MODAL */}
      <AnimatePresence>
        {payModal && (
          <PayModal
            invoice={payModal}
            onClose={() => setPayModal(null)}
            session={session}
            preset={preset}
            refresh={refresh}
            theme={theme}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   Card primitives
   ============================================================ */
const KpiCard = ({ label, value, prefix = "", theme, Icon, testid }) => (
  <motion.div
    data-testid={testid}
    whileHover={{ y: -3 }}
    className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
  >
    <div className="flex items-center justify-between mb-2">
      <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{label}</div>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center"
           style={{ background: theme.primarySoft, color: theme.primary }}>
        <Icon size={14} />
      </div>
    </div>
    <AnimatedNumber value={value} prefix={prefix} className="text-2xl font-bold text-slate-900 tabular-nums" />
  </motion.div>
);

const InteractiveCard = ({ testid, Icon, title, stat, theme, onClick }) => (
  <motion.button
    data-testid={testid}
    onClick={onClick}
    whileHover={{ y: -4, boxShadow: "0 20px 40px -20px rgba(15, 23, 42, 0.25)" }}
    whileTap={{ scale: 0.98 }}
    className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:border-slate-300 text-left group relative overflow-hidden"
  >
    {/* Accent bar */}
    <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
         style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})` }} />
    <div className="flex items-center justify-between mb-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
           style={{ background: theme.primarySoft, color: theme.primary }}>
        <Icon size={20} />
      </div>
      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
            style={{ background: "#F1F5F9", color: theme.primary }}>
        {stat}
      </span>
    </div>
    <div className="text-base font-bold text-slate-900 leading-tight">{title}</div>
    <div className="flex items-center gap-1 mt-2 text-[11px] font-semibold" style={{ color: theme.primary }}>
      Open live demo <ChevronRight size={12} className="group-hover:translate-x-1 transition-transform" />
    </div>
  </motion.button>
);

const PreviewCard = ({ Icon, label, desc, theme }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm relative overflow-hidden opacity-95 hover:opacity-100 transition-opacity">
    <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-slate-200" />
    <div className="flex items-center justify-between mb-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
        <Icon size={20} />
      </div>
      <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border"
            style={{ borderColor: theme.primarySoft, color: theme.primary, background: theme.primarySoft }}>
        Live in session
      </span>
    </div>
    <div className="text-base font-bold text-slate-900 leading-tight">{label}</div>
    <div className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">{desc}</div>
  </div>
);

const PREVIEW_TILES = [
  { Icon: Sparkles,    label: "Natural-Language UI Mods",     desc: "Prompt the platform to add a column, hide a field, or re-brand a button — live." },
  { Icon: UserSquare,  label: "Client / Patient Portals",     desc: "External surfaces for prospects to review jobs, approve quotes, upload files." },
  { Icon: ShieldCheck, label: "Role-Based Access (RBAC)",     desc: "Distinct views for Admin, Field Staff, and Client — enforced end-to-end." },
  { Icon: Database,    label: "Custom Schema on the Fly",     desc: "Add fields, tables, and relations without a migration or a developer." },
  { Icon: Lock,        label: "Native Auth · SSO · OTP",      desc: "Google, magic link, PIN, or hardware key — all first-class." },
  { Icon: Timer,       label: "Staff Time & Activity Logs",   desc: "Clock-in, job timers, billable-hour reconciliation." },
  { Icon: Package,     label: "Inventory & Service Catalog",  desc: "Stock levels, service packages, dynamic pricing tiers." },
  { Icon: Bug,         label: "Instant Logic Debugging",      desc: "Change business rules live — no dev push, no downtime." },
  { Icon: Building2,   label: "Multi-Location · Multi-Tenant",desc: "Branches, franchises, and divisions from one login." },
  { Icon: Cable,       label: "API & Legacy Connectivity",    desc: "Bridge to old databases, POS systems, and vendor APIs." },
];

/* ============================================================
   DRAWER · with Score 10 coaching overlay
   ============================================================ */
const Drawer = ({ children, onClose, title, theme, score, tutorialKey }) => {
  const [scoreOpen, setScoreOpen] = useState(false);
  useEffect(() => {
    if (!score) return;
    const t = setTimeout(() => setScoreOpen(true), 180);
    return () => clearTimeout(t);
  }, [score]);
  return (
  <>
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={onClose}
    />
    <motion.div
      initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 28, stiffness: 260 }}
      className="fixed right-0 top-0 h-full w-full max-w-xl bg-white z-50 shadow-2xl overflow-y-auto"
      data-testid="drawer"
    >
      <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: theme.primary }}>Live · Sandbox</div>
          <h3 className="text-lg font-bold text-slate-900 mt-0.5">{title}</h3>
        </div>
        <button onClick={onClose} data-testid="drawer-close" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors">
          <X size={18} />
        </button>
      </div>
      <div className="px-6 py-6 relative">
        {/* Per-capability tutorial video · plays inline at top of side panel */}
        {tutorialKey && <TutorialInline tutorialKey={tutorialKey} />}
        <AnimatePresence>
          {scoreOpen && score && (
            <Score10Overlay theme={theme} score={score} onDismiss={() => setScoreOpen(false)} />
          )}
        </AnimatePresence>
        {children}
      </div>
    </motion.div>
  </>
  );
};

/* ============================================================
   SCORE 10 · pinned coaching overlay
   ============================================================ */
const Score10Overlay = ({ theme, score, onDismiss }) => (
  <motion.div
    initial={{ y: -30, opacity: 0, scale: 0.95 }}
    animate={{ y: 0, opacity: 1, scale: 1 }}
    exit={{ y: -20, opacity: 0 }}
    transition={{ type: "spring", damping: 20, stiffness: 340 }}
    className="relative rounded-2xl mb-5 shadow-xl overflow-hidden"
    style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
    data-testid="score10-overlay"
  >
    <div className="absolute inset-0 opacity-20"
         style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.4), transparent 40%), radial-gradient(circle at 90% 80%, rgba(255,255,255,0.25), transparent 40%)" }} />
    <div className="relative p-5 text-white">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Star size={17} fill="white" stroke="white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">⭐ Score 10 Strategy</div>
            <div className="text-lg font-bold leading-tight mt-0.5">{score.title}</div>
          </div>
        </div>
        <button onClick={onDismiss} data-testid="score10-dismiss"
          className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/30 transition-colors flex items-center gap-1">
          Got it <X size={11} />
        </button>
      </div>
      <p className="text-sm leading-relaxed mt-3 opacity-95">{score.body}</p>
    </div>
  </motion.div>
);

/* ============================================================
   INDUSTRY 10-STEP NUMBERED REVENUE FLOW
   Same visual layout · steps + Score 10 vary per vertical.
   ============================================================ */
const IndustryFlow = ({ steps, theme, onOpen }) => (
  <div>
    <div className="mb-4 rounded-xl p-4 border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
             style={{ background: theme.primarySoft, color: theme.primary }}>
          <TrendingUp size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold tracking-[0.25em]" style={{ color: theme.primary }}>10-Step Revenue Flow</div>
          <div className="text-sm font-semibold text-slate-800">From first contact to lifetime customer — click any step for a Score 10 coaching card.</div>
        </div>
      </div>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
      {steps.map((s) => (
        <motion.button
          key={s.step}
          onClick={(e) => onOpen(s, e)}
          whileHover={{ y: -4, boxShadow: "0 20px 40px -20px rgba(15, 23, 42, 0.25)" }}
          whileTap={{ scale: 0.98 }}
          data-testid={`step-${s.step}`}
          className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm text-left group relative overflow-hidden"
        >
          <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
               style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})` }} />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full text-white"
                  style={{ background: theme.primary }}>
              Step {String(s.step).padStart(2, "0")}
            </span>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: theme.primarySoft, color: theme.primary }}>
              <s.Icon size={15} />
            </div>
          </div>
          <div className="text-sm font-bold text-slate-900 leading-snug">{s.title}</div>
          <div className="text-[10px] font-semibold mt-1.5" style={{ color: theme.primary }}>{s.stat}</div>
          <div className="mt-2 flex items-center gap-1 text-[10px] font-bold text-slate-500 group-hover:text-slate-800">
            Open · Score 10 <ChevronRight size={11} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </motion.button>
      ))}
    </div>
  </div>
);

/* ============================================================
   Common styled controls (light theme)
   ============================================================ */
const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500";
const btnPrimary = (theme) => ({
  className: "w-full px-4 py-2.5 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all disabled:opacity-50",
  style: { background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` },
});

/* ============================================================
   Drawer bodies
   ============================================================ */
const CrmBody = ({ preset, session, refresh, contacts }) => {
  const stages = preset.kanban_stages;
  const advance = async (c) => {
    const idx = stages.indexOf(c.data.stage);
    const next = stages[(idx + 1) % stages.length];
    await axios.patch(`${API}/sandbox/session/${session.id}/records/${c.id}`, { data: { stage: next } });
    await refresh(session.id);
    toast.success(`${c.data.name} → ${next}`);
  };
  return (
    <div className="space-y-2">
      {contacts.map(c => (
        <button key={c.id} onClick={() => advance(c)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 text-left transition-all">
          <div>
            <div className="font-semibold text-slate-800">{c.data.name}</div>
            <div className="text-xs text-slate-500">{c.data.business}</div>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 flex items-center gap-1">
            {c.data.stage} <ChevronRight size={11} />
          </span>
        </button>
      ))}
    </div>
  );
};

const IntakeBody = ({ preset, session, refresh, close }) => {
  const t = preset.terminology;
  const theme = INDUSTRY_THEME[session.industry];
  const [name, setName] = useState(""); const [busi, setBusi] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return toast.error(`${t.contact} name required`);
    setBusy(true);
    await axios.post(`${API}/sandbox/session/${session.id}/records`, {
      kind: "contact", data: { name: name.trim(), business: busi.trim(), stage: preset.kanban_stages[0] },
    });
    await refresh(session.id); setBusy(false); close(); toast.success(`New ${t.contact.toLowerCase()} captured`);
  };
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1 block">{t.contact} name</label>
        <input value={name} onChange={e=>setName(e.target.value)} className={inputCls} placeholder={`e.g. Acme Corp`} data-testid="intake-name" />
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1 block">Business / notes</label>
        <input value={busi} onChange={e=>setBusi(e.target.value)} className={inputCls} placeholder="Optional detail" data-testid="intake-busi" />
      </div>
      <button data-testid="intake-submit" onClick={submit} disabled={busy} {...btnPrimary(theme)}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Capture Intake
      </button>
    </div>
  );
};

const PaymentsBody = ({ preset, invoices, onPay }) => {
  const open = invoices.filter(i => i.data.status !== "paid");
  if (open.length === 0) return <div className="text-center py-10 text-slate-500 text-sm">No open {preset.terminology.invoice.toLowerCase()}s. Issue one from the Invoicing tile first.</div>;
  return (
    <div className="space-y-2">
      {open.map(i => (
        <button key={i.id} data-testid={`pay-inv-${i.id}`} onClick={() => onPay(i)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-indigo-200 bg-indigo-50/40 hover:bg-indigo-50 text-left transition-all">
          <div>
            <div className="font-semibold text-slate-900">{i.data.contact}</div>
            <div className="text-xs text-slate-500">{preset.terminology.invoice} · Test mode</div>
          </div>
          <div className="text-indigo-700 font-bold flex items-center gap-1">
            ${Number(i.data.amount).toLocaleString()} <ArrowRight size={13} />
          </div>
        </button>
      ))}
    </div>
  );
};

const InvoicingBody = ({ preset, session, refresh, contacts, close }) => {
  const t = preset.terminology;
  const theme = INDUSTRY_THEME[session.industry];
  const [contact, setContact] = useState(""); const [amount, setAmount] = useState("");
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!contact || !amt) return toast.error(`${t.contact} + amount required`);
    await axios.post(`${API}/sandbox/session/${session.id}/records`, {
      kind: "invoice", data: { contact, amount: amt, status: "unpaid", issued_at: new Date().toISOString() },
    });
    await refresh(session.id); close(); toast.success(`${t.invoice} issued for $${amt}`);
  };
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1 block">{t.contact}</label>
        <select value={contact} onChange={e=>setContact(e.target.value)} className={inputCls} data-testid="inv-contact">
          <option value="">Choose {t.contact.toLowerCase()}…</option>
          {contacts.map(c => <option key={c.id} value={c.data.name}>{c.data.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1 block">Amount ($)</label>
        <input value={amount} onChange={e=>setAmount(e.target.value)} type="number" className={inputCls} placeholder="1500" data-testid="inv-amount" />
      </div>
      <button onClick={submit} {...btnPrimary(theme)} data-testid="inv-submit"><DollarSign size={13} /> Issue {t.invoice}</button>
    </div>
  );
};

const KanbanBody = ({ preset, session, refresh, jobs }) => {
  const stages = preset.kanban_stages;
  const [dragId, setDragId] = useState(null);
  const drop = async (stage) => {
    if (!dragId) return;
    await axios.patch(`${API}/sandbox/session/${session.id}/records/${dragId}`, { data: { stage } });
    setDragId(null);
    await refresh(session.id);
    toast.success(`Advanced to ${stage}`);
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {stages.map(s => (
        <div key={s}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => drop(s)}
          className="min-h-[220px] rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1 py-1">{s}</div>
          <div className="space-y-1.5">
            {jobs.filter(j => j.data.stage === s).map(j => (
              <div key={j.id}
                draggable
                onDragStart={() => setDragId(j.id)}
                data-testid={`kanban-card-${j.id}`}
                className="cursor-grab active:cursor-grabbing bg-white rounded-lg p-2.5 border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="text-xs font-semibold text-slate-800 truncate">{j.data.title}</div>
                <div className="text-[10px] text-slate-500 truncate">{j.data.contact}</div>
                <div className="text-xs font-bold text-indigo-700 mt-1">${j.data.value}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AnalyticsBody = ({ preset, kpis, activity }) => {
  const theme = INDUSTRY_THEME[preset.terminology === preset.terminology ? "field_services" : "field_services"];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-cyan-50">
          <div className="text-[10px] font-bold uppercase text-slate-500">{preset.kpi_labels.revenue}</div>
          <AnimatedNumber value={kpis.revenue_usd || 0} prefix="$" className="text-3xl font-bold text-slate-900" />
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50">
          <div className="text-[10px] font-bold uppercase text-slate-500">Actions</div>
          <AnimatedNumber value={kpis.actions_total || 0} className="text-3xl font-bold text-slate-900" />
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-24 p-3 bg-white rounded-xl border border-slate-200">
        {activity.slice(0, 14).reverse().map((_, i) => (
          <motion.div key={i}
            initial={{ height: 0 }}
            animate={{ height: `${25 + (i % 5) * 15 + Math.random() * 25}%` }}
            transition={{ delay: i * 0.05 }}
            className="flex-1 rounded-t-md bg-gradient-to-t from-indigo-500 to-cyan-400" />
        ))}
        {activity.length === 0 && <div className="text-xs text-slate-400 m-auto">Trigger actions to see the chart populate</div>}
      </div>
    </div>
  );
};

const SchedulingBody = ({ preset, session, refresh, appointments, contacts, close }) => {
  const t = preset.terminology;
  const theme = INDUSTRY_THEME[session.industry];
  const slots = ["9:00 AM", "11:30 AM", "2:00 PM", "4:15 PM"];
  const [contact, setContact] = useState(""); const [slot, setSlot] = useState(slots[0]);
  const book = async () => {
    if (!contact) return toast.error(`Pick a ${t.contact.toLowerCase()}`);
    await axios.post(`${API}/sandbox/session/${session.id}/records`, {
      kind: "appointment", data: { contact, at: `Today · ${slot}`, service: t.appointment },
    });
    await refresh(session.id); close(); toast.success(`${t.appointment} confirmed for ${contact}`);
  };
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1 block">{t.contact}</label>
        <select value={contact} onChange={e=>setContact(e.target.value)} className={inputCls} data-testid="sched-contact">
          <option value="">Pick {t.contact.toLowerCase()}…</option>
          {contacts.map(c => <option key={c.id} value={c.data.name}>{c.data.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-2 block">Available slots</label>
        <div className="grid grid-cols-2 gap-2">
          {slots.map(s => (
            <button key={s} onClick={() => setSlot(s)}
              data-testid={`slot-${s}`}
              className={`px-3 py-2 rounded-lg font-semibold text-sm border-2 transition-all ${
                slot === s ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 hover:border-indigo-300"
              }`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <button onClick={book} {...btnPrimary(theme)} data-testid="sched-book"><Calendar size={13} /> Confirm {t.appointment}</button>
    </div>
  );
};

const WorkflowBody = ({ preset, session, refresh, fireRipple }) => {
  const theme = INDUSTRY_THEME[session.industry];
  const [busy, setBusy] = useState(false);
  const trigger = async (e) => {
    setBusy(true); fireRipple(e);
    await axios.post(`${API}/sandbox/session/${session.id}/workflow/trigger`, {
      when_field: "stage",
      when_value: preset.kanban_stages[preset.kanban_stages.length - 1],
      then_action: "send_notification",
      then_message: `Auto-notify: ${preset.terminology.job} closed`,
    });
    await refresh(session.id); setBusy(false);
    toast.success("⚡ Workflow fired · check the activity rail");
  };
  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 font-mono text-sm space-y-1">
        <div><span className="text-amber-700 font-bold">WHEN</span> <span className="text-slate-600">{preset.terminology.job.toLowerCase()}.stage ==</span> <span className="text-slate-900 font-semibold">&ldquo;{preset.kanban_stages[preset.kanban_stages.length - 1]}&rdquo;</span></div>
        <div><span className="text-emerald-700 font-bold">THEN</span> <span className="text-slate-600">send SMS + email + log CSAT survey</span></div>
      </div>
      <button onClick={trigger} disabled={busy} {...btnPrimary(theme)} data-testid="wf-trigger">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Fire Rule Now
      </button>
    </div>
  );
};

const DocsBody = ({ preset, session, refresh, docs }) => {
  const theme = INDUSTRY_THEME[session.industry];
  const fake = ["Insurance-Cert.pdf", "PO-4471.xlsx", "Photo-Site-A.jpg", "Contract-v2.docx"];
  const upload = async () => {
    const name = fake[Math.floor(Math.random() * fake.length)];
    await axios.post(`${API}/sandbox/session/${session.id}/records`, {
      kind: "document", data: { name, size_kb: 120 + Math.floor(Math.random() * 900) },
    });
    await refresh(session.id);
    toast.success(`Filed ${name}`);
  };
  return (
    <div className="space-y-3">
      <button onClick={upload} {...btnPrimary(theme)} data-testid="doc-upload"><Plus size={13} /> Simulate Upload</button>
      <div className="space-y-1.5">
        {docs.map(d => (
          <div key={d.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200">
            <span className="text-sm text-slate-700 truncate mr-2">{d.data.name}</span>
            <span className="text-[10px] font-bold text-slate-500 shrink-0">{d.data.size_kb} KB</span>
          </div>
        ))}
        {docs.length === 0 && <div className="text-center py-6 text-slate-400 text-sm">No files yet.</div>}
      </div>
    </div>
  );
};

const NotifyBody = ({ preset, session, refresh, fireRipple }) => {
  const theme = INDUSTRY_THEME[session.industry];
  const [channel, setChannel] = useState("email");
  const send = async (e) => {
    fireRipple(e);
    await axios.post(`${API}/sandbox/session/${session.id}/workflow/trigger`, {
      when_field: "channel", when_value: channel,
      then_action: `notify_${channel}`,
      then_message: `Broadcast ${channel} to ${preset.terminology.contact_plural.toLowerCase()}`,
    });
    await refresh(session.id);
    toast.success(`📣 ${channel.toUpperCase()} broadcast dispatched to ${preset.terminology.contact_plural.toLowerCase()}`);
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {["email", "sms"].map(c => (
          <button key={c} onClick={() => setChannel(c)}
            data-testid={`notify-ch-${c}`}
            className={`flex-1 px-3 py-2.5 rounded-lg font-bold text-sm border-2 transition-all ${
              channel === c ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 hover:border-indigo-300 text-slate-600"
            }`}>
            {c.toUpperCase()}
          </button>
        ))}
      </div>
      <button onClick={send} {...btnPrimary(theme)} data-testid="notify-send"><Bell size={13} /> Broadcast to {preset.terminology.contact_plural}</button>
    </div>
  );
};

/* ============================================================
   ACTIVITY RAIL
   ============================================================ */
const ActivityRail = ({ activity, theme }) => (
  <aside className="lg:sticky lg:top-32 h-fit">
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-4">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <div className="text-[10px] uppercase font-bold tracking-[0.25em] text-slate-700">Live System Feed</div>
      </div>
      <div className="space-y-2 max-h-[560px] overflow-auto pr-1" data-testid="activity-feed">
        <AnimatePresence initial={false}>
          {activity.length === 0 && <div className="text-xs text-slate-400 py-6 text-center">Nothing yet — take an action.</div>}
          {activity.map(a => (
            <motion.div key={a.id}
              initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
              className="border-l-4 pl-3 py-1.5" style={{ borderColor: theme.primary }}>
              <div className="text-xs text-slate-700 leading-snug">
                <span className="font-semibold" style={{ color: theme.primary }}>{a.verb}</span>{" "}
                <span className="font-medium text-slate-900">{a.subject}</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                {new Date(a.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                {a.meta?.amount && <> · ${Number(a.meta.amount).toLocaleString()}</>}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  </aside>
);

/* ============================================================
   STRIPE MODAL · flip card + confetti
   ============================================================ */
const PayModal = ({ invoice, onClose, session, preset, refresh, theme }) => {
  const [card] = useState("4242 4242 4242 4242");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pay = async () => {
    setBusy(true);
    await new Promise(r => setTimeout(r, 1200));
    await axios.post(`${API}/sandbox/session/${session.id}/pay`, {
      invoice_id: invoice.id, card_last4: "4242",
    });
    setBusy(false); setDone(true);
    await refresh(session.id);
    setTimeout(onClose, 2000);
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center px-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
        transition={{ type: "spring", damping: 22 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative"
        onClick={e => e.stopPropagation()}
        data-testid="pay-modal"
      >
        {/* Confetti burst on success */}
        {done && <ConfettiBurst />}

        <div className="p-6 border-b border-slate-100">
          <div className="text-[10px] uppercase font-bold tracking-[0.25em]" style={{ color: theme.primary }}>Stripe · Test Mode</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">Charge {preset.terminology.contact}</div>
          <div className="text-sm text-slate-500 mt-1">{invoice.data.contact} · <span className="font-bold text-slate-900">${Number(invoice.data.amount).toLocaleString()}</span></div>
        </div>

        <div className="p-6">
          {/* Card visual · flips on payment */}
          <div className="perspective-[1200px]">
            <motion.div
              animate={{ rotateY: done ? 180 : 0 }}
              transition={{ duration: 0.7 }}
              className="relative w-full h-44 rounded-2xl [transform-style:preserve-3d] mb-5"
            >
              {/* FRONT */}
              <div
                className="absolute inset-0 rounded-2xl p-5 text-white [backface-visibility:hidden] shadow-xl"
                style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
              >
                <div className="flex justify-between items-start">
                  <div className="w-10 h-7 bg-white/30 rounded-md" />
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Test</div>
                </div>
                <div className="text-lg font-mono tracking-[0.2em] mt-6 tabular-nums">{card}</div>
                <div className="flex justify-between mt-4 text-[11px] uppercase font-semibold">
                  <div>
                    <div className="opacity-60">Card holder</div>
                    <div>Nex Sandbox</div>
                  </div>
                  <div>
                    <div className="opacity-60">Expires</div>
                    <div>12 / 28</div>
                  </div>
                </div>
              </div>
              {/* BACK */}
              <div className="absolute inset-0 rounded-2xl [backface-visibility:hidden] [transform:rotateY(180deg)] flex flex-col items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-xl">
                <CheckCircle2 size={56} strokeWidth={2} className="mb-2" />
                <div className="text-[11px] uppercase font-bold tracking-[0.25em]">Payment Successful</div>
                <div className="text-sm opacity-90 mt-1">${Number(invoice.data.amount).toLocaleString()} captured · KPIs ticking</div>
              </div>
            </motion.div>
          </div>

          {!done && (
            <button onClick={pay} disabled={busy} {...btnPrimary(theme)} className={btnPrimary(theme).className + " !py-3.5"} data-testid="pay-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : <><CreditCard size={14} /> Pay ${Number(invoice.data.amount).toLocaleString()}</>}
            </button>
          )}
          <div className="text-[10px] text-slate-400 mt-3 text-center">Test-mode simulation · no real card is charged</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ============================================================
   CONFETTI · lightweight, pure DOM particles
   ============================================================ */
const CONFETTI_COLORS = ["#6366F1", "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#EC4899"];
const ConfettiBurst = () => {
  const pieces = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    id: i,
    x: (Math.random() - 0.5) * 400,
    y: -50 - Math.random() * 300,
    rot: Math.random() * 720,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.random() * 0.2,
  })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-30">
      {pieces.map(p => (
        <motion.div key={p.id}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          animate={{ x: p.x, y: p.y + 400, rotate: p.rot, opacity: 0 }}
          transition={{ duration: 1.8, delay: p.delay, ease: "easeOut" }}
          className="absolute left-1/2 top-1/2 w-2 h-3 rounded-sm"
          style={{ background: p.color }}
        />
      ))}
    </div>
  );
};

export default SandboxPage;
